"use client";
import { ArrowLeft, ArrowRight, Play } from "lucide-react";
import Image from "next/image";
import CustomButton from "./CustomButton";
import AnimateOnScroll from "./AnimateOnScroll";
import { useRef, useState, useEffect } from "react";
import Link from "next/link";
import parse from "html-react-parser";
import { SITE_URL } from "@/lib/metadata";

const PodcastCard = ({ podcast }: { podcast: any }) => (
  <article
    className="group w-[82vw] shrink-0 rounded-[12.86px] bg-white/10 p-[8.57px] sm:w-[420px] xl:w-[500px] 2xl:w-[550px] 3xl:w-[610px]"
    aria-label={`Podcast: ${podcast.title}`}
  >
    {/* Inner frame */}
    <Link
      href={podcast.slug ? `/${podcast.slug}` : podcast.href || "#"}
      className="relative block h-[343px] w-full overflow-hidden rounded-[6.86px]"
    >
      {/* Background image */}
      <Image
        src={podcast.featuredImage || "/images/v3/home/podcast-thumb.jpg"}
        alt=""
        fill
        className="object-cover transition-transform duration-300 group-hover:scale-105"
        sizes="592px"
        aria-hidden
      />

      {/* Blue gradient at bottom */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#003480e6] from-[0%] to-transparent to-[50%]" />

      {/* Content — bottom 160px */}
      <div className="absolute bottom-0 left-0 right-0 flex h-[160px] flex-col justify-end gap-2 p-4">
        {/* Host row */}
        <div className="flex items-center gap-2">
          <div className="relative h-7 w-7 shrink-0 overflow-hidden rounded-full border border-white/30">
            <Image
              src="/images/team/alex.png"
              alt="Alex Rodriguez"
              fill
              className="object-cover"
              sizes="28px"
            />
          </div>
          <span className="font-onest text-[16px] font-normal text-white">
            Alex Rodriguez
          </span>
        </div>

        {/* Episode title */}
        <h3 className="font-crimsonText text-[32px] font-semibold leading-none text-white">
          {parse(podcast.title || "")}
        </h3>

        {/* Description */}
        <p className="line-clamp-2 font-onest text-[14px] font-light leading-[110%] text-white/80">
          {podcast.excerpt || podcast.description}
        </p>

        {/* Play controls */}
        <div className="mt-1 flex items-center gap-2">
          <div className="flex h-[41px] items-center gap-2 rounded-[8px] bg-white/10 px-3">
            <div className="flex h-[23px] w-[23px] items-center justify-center rounded-md bg-white/20">
              <Play
                size={10}
                className="translate-x-px fill-white text-white"
              />
            </div>
            <span className="font-onest text-[17px] font-light text-white">
              {podcast.duration || "15:00"}
            </span>
          </div>
        </div>
      </div>
    </Link>
  </article>
);

const ArticleCard = ({
  article,
  hideDesc = false,
}: {
  article: any;
  hideDesc?: boolean;
}) => (
  <article
    role="listitem"
    className="w-fit shrink-0 rounded-[16px] bg-white/30 p-[10px] backdrop-blur-sm sm:w-[380px] lg:w-[calc((100%-40px)/3)]"
    aria-label={`Article: ${article.title}`}
  >
    {/* Inner white card */}
    <div className="flex h-[380px] flex-col overflow-hidden rounded-[10px] bg-white 3xl:h-[480px]">
      {/* Article image */}
      <div className="relative h-[200px] w-full shrink-0 3xl:h-[262px]">
        <Image
          src={article.featuredImage || "/images/v3/home/article-thumbnail.png"}
          alt=""
          fill
          className="object-cover"
          sizes="(max-width: 1024px) 380px, 500px"
          aria-hidden
        />
      </div>

      {/* Text content */}
      <div className="flex flex-1 flex-col justify-between px-[10px] py-[10px]">
        <div className="flex flex-col">
          {/* Title */}
          <div className="px-[10px] py-[8.5px]">
            <h3 className="font-crimsonText text-[28px] font-semibold leading-none text-black">
              {parse(article.title || "")}
            </h3>
          </div>

          {/* Description */}
          {!hideDesc && (
            <div className="px-[10px] py-[10px]">
              <p className="line-clamp-3 font-onest text-[16px] font-light leading-[110%] text-black/70">
                {article.excerpt || article.description}
              </p>
            </div>
          )}
        </div>

        {/* Read link */}
        <div className="px-[10px] pb-[10px]">
          <Link
            href={article.slug ? `/${article.slug}` : article.href || "#"}
            className="inline-flex items-center rounded-[8px] bg-black/10 px-[10px] py-[10px] font-onest text-[16px] font-light text-black transition-colors hover:bg-black/20"
            aria-label={`Read article: ${article.title}`}
          >
            Read article now
          </Link>
        </div>
      </div>
    </div>
  </article>
);

const PODCAST_CARD_W = 610;
const PODCAST_GAP = 20;

const Resources = () => {
  const podcastRef = useRef<HTMLDivElement>(null);
  const articleRef = useRef<HTMLDivElement>(null);
  const [podcastsList, setPodcastsList] = useState<any[]>([]);
  const [articlesList, setArticlesList] = useState<any[]>([]);

  useEffect(() => {
    fetch("/api/podcasts?page=1&perPage=6")
      .then((res) => res.json())
      .then((data) => {
        if (data && data.posts && data.posts.length > 0) {
          setPodcastsList(data.posts);
        }
      })
      .catch((err) => console.error("Error loading v3 podcasts:", err));

    fetch("/api/posts?page=1&perPage=6")
      .then((res) => res.json())
      .then((data) => {
        if (data && data.posts && data.posts.length > 0) {
          setArticlesList(data.posts);
        }
      })
      .catch((err) => console.error("Error loading v3 articles:", err));
  }, []);

  const scrollPodcast = (dir: 1 | -1) => {
    if (!podcastRef.current) return;
    const firstCard = podcastRef.current.firstElementChild as HTMLElement;
    const cardW = firstCard ? firstCard.offsetWidth : PODCAST_CARD_W;
    podcastRef.current.scrollBy({
      left: dir * (cardW + PODCAST_GAP),
      behavior: "smooth",
    });
  };

  const scrollArticle = (dir: 1 | -1) => {
    if (articleRef.current) {
      const firstCard = articleRef.current.firstElementChild as HTMLElement;
      const cardWidth = firstCard ? firstCard.offsetWidth : 490;
      articleRef.current.scrollBy({
        left: dir * (cardWidth + 20),
        behavior: "smooth",
      });
    }
  };

  const resourcesSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "ItemList",
        name: "Dose Of Growth Podcast Episodes",
        itemListElement: podcastsList.map((p, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: new URL(p.slug ? `/${p.slug}` : p.href || "/", SITE_URL).href,
          name: p.title,
        })),
      },
      {
        "@type": "ItemList",
        name: "Counselling & Therapy Blog Articles",
        itemListElement: articlesList.map((a, i) => ({
          "@type": "ListItem",
          position: i + 1,
          url: new URL(a.slug ? `/${a.slug}` : a.href || "/", SITE_URL).href,
          name: a.title,
        })),
      },
    ],
  };

  return (
    <div>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(resourcesSchema).replace(/</g, "\\u003c"),
        }}
      />
      <section
        className="relative w-full overflow-hidden bg-[#021837]"
        aria-labelledby="podcast-heading"
      >
        <Image
          src="/images/v3/home/resources-bg.png"
          alt=""
          fill
          className="object-cover object-center"
          sizes="100vw"
          aria-hidden
          priority
        />
        {/* podcast section */}
        <div className="container relative">
          {/* Header */}
          <AnimateOnScroll className="flex flex-col gap-5 lg:mb-5 3xl:mb-10">
            <span
              data-from-left
              className="font-crimsonText text-[20px] font-semibold text-white/80 lg:text-[28px]"
            >
              Dose Of Growth Podcast by Alexander Rodriguez
            </span>
            <h2 id="podcast-heading" className="heading text-white">
              Mental Health and Counselling Podcast
            </h2>
            <p className="max-w-[897px] font-onest text-[18px] font-normal leading-[110%] text-white/70">
              Explore podcast episodes featuring honest conversations, practical
              insights, and supportive guidance from a professional counsellor.
              Tune in for expert discussions on mental health, relationships,
              personal growth, and navigating life’s difficult moments.
            </p>
            <div>
              <CustomButton
                label="View All Podcasts"
                href="/podcast"
                variant="primary"
                withIcon
                size="lg"
              />
            </div>
          </AnimateOnScroll>

          {/* Nav arrows + scrollable cards */}
          {podcastsList.length > 0 && (
            <div className="flex flex-col items-end gap-4">
              <div
                className="flex gap-[27px]"
                role="group"
                aria-label="Podcast navigation"
              >
                <button
                  onClick={() => scrollPodcast(-1)}
                  className="flex items-center justify-center rounded-[14px] border border-white/30 bg-white/10 text-white transition-colors hover:bg-white/20 lg:size-[50px] lg:rounded-xl 3xl:size-[70px] 3xl:rounded-2xl"
                  aria-label="Previous podcasts"
                >
                  <ArrowLeft size={20} strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => scrollPodcast(1)}
                  className="flex items-center justify-center rounded-[14px] border border-white/30 bg-white/10 text-white transition-colors hover:bg-white/20 lg:size-[50px] lg:rounded-xl 3xl:size-[70px] 3xl:rounded-2xl"
                  aria-label="Next podcasts"
                >
                  <ArrowRight size={20} strokeWidth={1.5} />
                </button>
              </div>

              <div
                ref={podcastRef}
                className="flex w-full gap-[20px] overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                role="list"
                aria-label="Podcast episodes"
              >
                {podcastsList.map((p, idx) => (
                  <div key={p.id || idx} role="listitem">
                    <PodcastCard podcast={p} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* article */}
        <div className="container relative">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:justify-end">
            <AnimateOnScroll className="flex w-full flex-col gap-4 lg:mb-5 lg:max-w-[760px] 3xl:mb-10">
              <span
                data-from-left
                className="font-crimsonText text-[20px] font-bold text-v3-highlight lg:text-[28px]"
              >
                Articles by Alexander Rodriguez
              </span>
              <h2 id="articles-heading" className="heading text-v3-secondary">
                Our Latest
                <br />
                Articles & Insights
              </h2>
              <p className="font-onest text-[18px] font-normal leading-[110%] text-black/50">
                Explore thoughtful articles on counselling, relationship
                dynamics, mental health, and personal growth. Find practical
                advice on managing anxiety, navigating grief, building
                confidence, and moving forward with clarity.
              </p>
              <div>
                <CustomButton
                  label="Articles"
                  href="/wellness-blog"
                  variant="primary"
                  withIcon
                  size="lg"
                />
              </div>
            </AnimateOnScroll>
          </div>

          {/* Nav arrows + article cards */}
          {articlesList.length > 0 && (
            <div className="flex flex-col items-end gap-4">
              <div
                className="flex gap-[27px]"
                role="group"
                aria-label="Article navigation"
              >
                <button
                  onClick={() => scrollArticle(-1)}
                  className="flex items-center justify-center rounded-[14px] border border-v3-secondary/20 bg-black/10 text-v3-secondary transition-colors hover:bg-black/20 lg:size-[50px] lg:rounded-xl 3xl:size-[70px] 3xl:rounded-2xl"
                  aria-label="Previous articles"
                >
                  <ArrowLeft className="text-white" size={20} strokeWidth={1.5} />
                </button>
                <button
                  onClick={() => scrollArticle(1)}
                  className="flex items-center justify-center rounded-[14px] border border-v3-secondary/20 bg-black/10 text-v3-secondary transition-colors hover:bg-black/20 lg:size-[50px] lg:rounded-xl 3xl:size-[70px] 3xl:rounded-2xl"
                  aria-label="Next articles"
                >
                  <ArrowRight
                    className="text-white"
                    size={20}
                    strokeWidth={1.5}
                  />
                </button>
              </div>

              <div className="relative w-full">
                <div
                  ref={articleRef}
                  className="flex w-full gap-[20px] overflow-x-auto scroll-smooth [scrollbar-width:none] lg:[-webkit-mask-image:linear-gradient(to_right,transparent_20%,black_400px)] lg:[mask-image:linear-gradient(to_right,transparent_20%,black_400px)] [&::-webkit-scrollbar]:hidden"
                  role="list"
                  aria-label="Articles"
                >
                  {articlesList.map((a, idx) => (
                    <ArticleCard key={a.id || idx} article={a} hideDesc={true} />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Resources;
